import { clampDateTo, datePl, MEMBERSHIP_KINDS, type MembershipChangeDto, type MeDto, type TeamDto, todayInWarsaw } from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Avatar, Badge, Box, Button, Card, ColorInput, Group, Loader, Menu, Modal, MultiSelect, SegmentedControl, Select, SimpleGrid, Stack, Switch, Text, TextInput } from '@mantine/core';
import { IconArrowsExchange, IconGripVertical, IconPlus, IconUserPlus } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { TradeBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';
import { useTrades } from '../../data';
import { ask } from '../../confirm';
import { notifyError, notifyOk } from '../../notify';
import { ConflictList, usePeople, useTeams } from './common';
import { type MoveRequest, useTeamMove } from './TeamMove';

function TeamForm({ team, onClose }: { team: TeamDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const people = usePeople();
  const trades = useTrades();
  const [f, setF] = useState({ name: team?.name ?? '', color: team?.color ?? '#1c7ed6', leaderId: team?.leaderId ?? null, tradeCodes: team?.tradeCodes ?? [], active: team?.active ?? true });
  const save = useMutation({
    mutationFn: () => api<TeamDto>(team ? `/teams/${team.id}` : '/teams', { method: team ? 'PUT' : 'POST', body: f }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
      void qc.invalidateQueries({ queryKey: ['planning-board'] });
      notifyOk(team ? 'Zapisano ekipę' : 'Dodano ekipę');
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={team ? 'Edycja ekipy' : 'Nowa ekipa'}>
      <Stack>
        <TextInput label="Nazwa" value={f.name} onChange={(e) => setF({ ...f, name: e.currentTarget.value })} data-autofocus />
        <ColorInput label="Kolor" value={f.color} onChange={(v) => setF({ ...f, color: v })} format="hex" />
        <Select label="Lider" description="Jeśli nie jest w składzie ekipy — dołączy do stałego składu od dziś" clearable searchable data={(people.data ?? []).map((p) => ({ value: p.id, label: p.fullName }))} value={f.leaderId} onChange={(v) => setF({ ...f, leaderId: v })} />
        <MultiSelect label="Branże" data={(trades.data ?? []).map((t) => ({ value: t.code, label: t.name }))} value={f.tradeCodes} onChange={(v) => setF({ ...f, tradeCodes: v })} />
        {team && <SegmentedControl value={f.active ? 't' : 'n'} onChange={(v) => setF({ ...f, active: v === 't' })} data={[{ value: 't', label: 'Aktywna' }, { value: 'n', label: 'Nieaktywna' }]} />}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={f.name.trim().length < 2}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Wynik zmiany składu: które zadania przeliczono i jakie konflikty powstały. */
function AffectedReport({ r, onClose }: { r: MembershipChangeDto; onClose: () => void }) {
  return (
    <Modal opened onClose={onClose} title="Przeliczone zadania" size="lg">
      <Stack>
        {r.affected.length === 0 ? (
          <Text size="sm">Zmiana nie wpłynęła na zaplanowane zadania.</Text>
        ) : (
          r.affected.map((a) => (
            <Card key={a.taskId} withBorder padding="sm">
              <Text fw={600} size="sm">
                {a.number} — {a.title}
              </Text>
              {a.added.length > 0 && <Text size="sm">Doszli: {a.added.join(', ')}</Text>}
              {a.removed.length > 0 && <Text size="sm">Wypadli: {a.removed.join(', ')}</Text>}
              {a.conflicts.length > 0 && (
                <Alert color="red" mt="xs" title="Nowe konflikty">
                  <ConflictList conflicts={a.conflicts} />
                </Alert>
              )}
            </Card>
          ))
        )}
        <Text size="xs" c="dimmed">
          Przeliczane są tylko dni przyszłe i przydziały „przez ekipę”. Pracownicy dodani ręcznie i dni minione (przydziały faktyczne) się nie zmieniają.
        </Text>
        <Group justify="flex-end">
          <Button onClick={onClose}>Zamknij</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function MemberForm({ team, onDone, onClose }: { team: TeamDto; onDone: (r: MembershipChangeDto) => void; onClose: () => void }) {
  const people = usePeople();
  const qc = useQueryClient();
  const [f, setF] = useState({ userId: null as string | null, kind: 'stale' as 'stale' | 'czasowe', validFrom: todayInWarsaw(), validTo: '', note: '' });
  const save = useMutation({
    mutationFn: () => api<MembershipChangeDto>(`/teams/${team.id}/members`, { method: 'POST', body: { ...f, validTo: f.validTo || null } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
      void qc.invalidateQueries({ queryKey: ['planning-board'] });
      notifyOk('Dodano do ekipy');
      onDone(r);
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={`Dodanie do ekipy: ${team.name}`}>
      <Stack>
        <SegmentedControl value={f.kind} onChange={(v) => setF({ ...f, kind: v as typeof f.kind })} data={Object.entries(MEMBERSHIP_KINDS).map(([value, label]) => ({ value, label }))} />
        <Select label="Pracownik" searchable data={(people.data ?? []).map((p) => ({ value: p.id, label: `${p.fullName}${p.isSubcontractor ? ' (podwykonawca)' : ''}` }))} value={f.userId} onChange={(v) => setF({ ...f, userId: v })} />
        <Group grow>
          <TextInput type="date" label="Od" value={f.validFrom} onChange={(e) => setF({ ...f, validFrom: e.currentTarget.value, validTo: clampDateTo(e.currentTarget.value, f.validTo, { keepEmpty: f.kind !== 'czasowe' }) })} />
          <TextInput type="date" label={f.kind === 'czasowe' ? 'Do' : 'Do (opcjonalnie)'} value={f.validTo} min={f.validFrom || undefined} onChange={(e) => setF({ ...f, validTo: e.currentTarget.value })} />
        </Group>
        <TextInput label="Notatka" placeholder={f.kind === 'czasowe' ? 'np. pomoc przy montażu PV' : ''} value={f.note} onChange={(e) => setF({ ...f, note: e.currentTarget.value })} />
        {f.kind === 'czasowe' && (
          <Text size="xs" c="dimmed">
            Czasowe przejście: w tych dniach pracownik pracuje w tej ekipie i wypada ze swojej stałej ekipy.
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!f.userId || (f.kind === 'czasowe' && !f.validTo)}>
            Dodaj do ekipy
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Ekipy i ich skład w czasie (stały skład, czasowe przejścia, zakończenia — bez usuwania historii). */
export function TeamsPage({ me }: { me: MeDto }) {
  const teams = useTeams();
  const qc = useQueryClient();
  const edit = can(me, 'ekipy.zarzadzanie');
  const [form, setForm] = useState<TeamDto | 'new' | null>(null);
  const [member, setMember] = useState<TeamDto | null>(null);
  const [report, setReport] = useState<MembershipChangeDto | null>(null);
  const [ending, setEnding] = useState<{ id: string; name: string; date: string } | null>(null);
  const today = todayInWarsaw();
  const end = useMutation({
    mutationFn: (v: { id: string; validTo: string }) => api<MembershipChangeDto>(`/team-memberships/${v.id}/end`, { method: 'POST', body: { validTo: v.validTo } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
      void qc.invalidateQueries({ queryKey: ['planning-board'] });
      notifyOk('Zakończono członkostwo');
      setReport(r);
    },
    onError: notifyError,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api<MembershipChangeDto>(`/team-memberships/${id}/remove`, { method: 'POST' }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
      void qc.invalidateQueries({ queryKey: ['planning-board'] });
      notifyOk('Usunięto z ekipy');
      if (r.affected.length) setReport(r);
    },
    onError: notifyError,
  });
  const [showPast, setShowPast] = useState(false);
  // przeciąganie pracownika między ekipami (mysz); na telefonie — menu „Przenieś do…”
  const mover = useTeamMove(setReport);
  const [drag, setDrag] = useState<Omit<MoveRequest, 'to'> | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const canDropOn = (t: TeamDto) => !!drag && t.active && drag.from.id !== t.id;
  const dropOn = (t: TeamDto) => {
    if (drag && canDropOn(t)) void mover.start({ ...drag, to: t });
    setDrag(null);
    setOver(null);
  };

  return (
    <>
      <PageHeader
        title="Ekipy"
        description={`Skład ekip w czasie. Zmiana składu przelicza przyszłe przydziały zaplanowanych zadań; historia (kto faktycznie pracował) zostaje.${edit ? ' Pracownika przeniesiesz, przeciągając go na inną ekipę (albo ikoną strzałek).' : ''}`}
        actions={
          edit && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setForm('new')}>
              Nowa ekipa
            </Button>
          )
        }
      />
      <Switch label="Pokaż byłych członków ekip" checked={showPast} onChange={(e) => setShowPast(e.currentTarget.checked)} mb="md" />
      <QueryState isLoading={teams.isLoading} error={teams.error}>
        <SimpleGrid cols={{ base: 1, lg: 2 }}>
          {teams.data?.map((t) => (
            <Card
              key={t.id}
              style={{
                borderTop: `3px solid ${t.color}`,
                opacity: t.active ? 1 : 0.6,
                outline: over === t.id ? `2px solid ${t.color}` : canDropOn(t) ? '2px dashed var(--app-border)' : undefined,
                outlineOffset: 2,
                background: over === t.id ? `color-mix(in srgb, ${t.color} 10%, var(--mantine-color-body))` : undefined,
                transition: 'background 120ms, outline-color 120ms',
              }}
              onDragOver={(e) => {
                if (!canDropOn(t)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (over !== t.id) setOver(t.id);
              }}
              onDragLeave={(e) => {
                if (over === t.id && !e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                dropOn(t);
              }}
            >
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <Text fw={700}>{t.name}</Text>
                  {over === t.id && drag && (
                    <Badge variant="filled" color="eko">
                      Upuść: {drag.fullName}
                    </Badge>
                  )}
                  {mover.checking === t.id && <Loader size="xs" />}
                  {t.tradeCodes.map((c) => (
                    <TradeBadge key={c} code={c} />
                  ))}
                  {!t.active && <Badge color="gray">nieaktywna</Badge>}
                </Group>
                {edit && (
                  <Group gap={4}>
                    <Button size="xs" variant="subtle" onClick={() => setForm(t)}>
                      Edytuj
                    </Button>
                    <Button size="xs" variant="light" leftSection={<IconUserPlus size={14} />} onClick={() => setMember(t)}>
                      Dodaj do ekipy
                    </Button>
                  </Group>
                )}
              </Group>
              <Stack gap={2}>
                {t.members.filter((m) => showPast || !(m.validTo && m.validTo < today)).map((m) => {
                  const current = m.validFrom <= today && (!m.validTo || m.validTo >= today);
                  const future = m.validFrom > today;
                  const movable = edit && (current || future) && m.kind === 'stale' && !m.membershipId.startsWith('tmp-');
                  const targets = (teams.data ?? []).filter((x) => x.active && x.id !== t.id);
                  const dragged = drag?.membershipId === m.membershipId || mover.moving?.membershipId === m.membershipId;
                  return (
                    <Group
                      key={m.membershipId}
                      gap="sm"
                      wrap="nowrap"
                      py={6}
                      px={4}
                      draggable={movable}
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', m.fullName);
                        setDrag({ membershipId: m.membershipId, userId: m.userId, fullName: m.fullName, from: t });
                      }}
                      onDragEnd={() => {
                        setDrag(null);
                        setOver(null);
                      }}
                      title={movable ? 'Przeciągnij na inną ekipę, aby przenieść' : undefined}
                      style={{ opacity: dragged ? 0.35 : current || future ? 1 : 0.45, borderBottom: '1px solid var(--app-border)', cursor: movable ? 'grab' : undefined }}
                    >
                      {movable && (
                        <Box visibleFrom="sm" lh={0} style={{ color: 'var(--mantine-color-dimmed)', flexShrink: 0 }} aria-hidden>
                          <IconGripVertical size={14} />
                        </Box>
                      )}
                      <Avatar size={32} radius="xl" variant="light" color={m.kind === 'czasowe' ? 'orange' : 'eko'}>
                        {m.fullName
                          .split(' ')
                          .map((x) => x[0])
                          .slice(0, 2)
                          .join('')}
                      </Avatar>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Group gap={6} wrap="nowrap">
                          <Anchor component={Link} to={`/pracownicy/${m.userId}`} size="sm" fw={600} truncate draggable={false}>
                            {m.fullName}
                          </Anchor>
                          {t.leaderId === m.userId && (
                            <Badge size="xs" variant="outline" color="gray">
                              lider
                            </Badge>
                          )}
                          {m.kind === 'czasowe' && (
                            <Badge size="xs" variant="light" color="orange">
                              czasowo
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed" truncate>
                          {datePl(m.validFrom)} – {m.validTo ? datePl(m.validTo) : 'nadal'}
                          {m.note ? ` · ${m.note}` : ''}
                        </Text>
                      </div>
                      {edit && (current || future) && (
                        <Group gap={2} wrap="nowrap">
                          {movable && targets.length > 0 && (
                            <Menu position="bottom-end" withinPortal>
                              <Menu.Target>
                                <ActionIcon variant="subtle" color="gray" size="sm" aria-label={`Przenieś ${m.fullName} do innej ekipy`} title="Przenieś do…">
                                  <IconArrowsExchange size={14} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Label>Przenieś do…</Menu.Label>
                                {targets.map((x) => (
                                  <Menu.Item
                                    key={x.id}
                                    leftSection={<span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: x.color }} />}
                                    onClick={() => void mover.start({ membershipId: m.membershipId, userId: m.userId, fullName: m.fullName, from: t, to: x })}
                                  >
                                    {x.name}
                                  </Menu.Item>
                                ))}
                              </Menu.Dropdown>
                            </Menu>
                          )}
                          {!m.validTo && (
                            <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setEnding({ id: m.membershipId, name: m.fullName, date: today })}>
                              Zakończ…
                            </Button>
                          )}
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            color="red"
                            loading={remove.isPending && remove.variables === m.membershipId}
                            onClick={() =>
                              void ask(
                                future
                                  ? `${m.fullName} miał(a) dołączyć do ekipy ${datePl(m.validFrom)} — wpis zostanie usunięty.`
                                  : `${m.fullName} od dziś nie należy do ekipy „${t.name}”. Historia pracy z poprzednich dni zostaje, przyszłe przydziały zostaną przeliczone.`,
                                { title: 'Usunąć z ekipy?', confirm: 'Usuń z ekipy', danger: true },
                              ).then((ok) => ok && remove.mutate(m.membershipId))
                            }
                          >
                            Usuń
                          </Button>
                        </Group>
                      )}
                    </Group>
                  );
                })}
              </Stack>
              {!t.members.some((m) => showPast || !(m.validTo && m.validTo < today)) && (
                <EmptyState
                  compact
                  title="Ekipa nie ma składu"
                  hint={edit ? 'Dodaj pracowników przyciskiem „Dodaj do ekipy”.' : undefined}
                />
              )}
            </Card>
          ))}
        </SimpleGrid>
        {teams.data?.length === 0 && (
          <Card withBorder>
            <EmptyState title="Brak ekip" hint={edit ? 'Dodaj pierwszą ekipę przyciskiem „Nowa ekipa”.' : undefined} />
          </Card>
        )}
      </QueryState>
      {form && <TeamForm team={form === 'new' ? null : form} onClose={() => setForm(null)} />}
      {member && (
        <MemberForm
          team={member}
          onClose={() => setMember(null)}
          onDone={(r) => {
            setMember(null);
            setReport(r);
          }}
        />
      )}
      {report && <AffectedReport r={report} onClose={() => setReport(null)} />}
      {mover.dialog}
      <Modal opened={!!ending} onClose={() => setEnding(null)} title={ending ? `Koniec w ekipie: ${ending.name}` : ''} size="sm">
        {ending && (
          <Stack>
            <TextInput type="date" label="Ostatni dzień w ekipie" value={ending.date} onChange={(e) => setEnding({ ...ending, date: e.currentTarget.value })} />
            <Text size="xs" c="dimmed">
              Przyszłe przydziały zaplanowanych zadań zostaną przeliczone; historia pracy zostaje.
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEnding(null)}>
                Anuluj
              </Button>
              <Button color="red" disabled={!/^\d{4}-\d{2}-\d{2}$/.test(ending.date)} loading={end.isPending} onClick={() => end.mutate({ id: ending.id, validTo: ending.date }, { onSuccess: () => setEnding(null) })}>
                Zakończ członkostwo
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
